import { ApolloError, useMutation } from '@apollo/client';
import { Alert, Button, ProgressCircle } from '@unmind/design-system';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { logException } from 'App/logging';
import { tracking } from 'App/Tracking';
import { multiDestinationLogger } from 'App/Tracking/multiDestinationLogger';
import { MutationStatusCode } from '__generated__/globalTypes';
import Modal from '../../../../Shared/Modal/Modal';
import {
  DAYS_AND_HOURS_UI_TYPE,
  INDIVIDUAL_UI_TYPE,
  WellbeingMeasureEntryPoint,
} from '../constants';
import { WellbeingMeasureQuestionnaire_wellbeingMeasureQuestionnaire as Questionnaire } from '../__generated__/WellbeingMeasureQuestionnaire';
import {
  SaveWellbeingMeasureResponses,
  SaveWellbeingMeasureResponsesVariables,
} from '../__generated__/SaveWellbeingMeasureResponses';
import {
  HOME_WELLBEING_MEASURE_STATUS_QUERY,
  SAVE_WELLBEING_MEASURE_RESPONSES_MUTATION,
} from '../wellbeingMeasureQueries';
import CompletionStage from './CompletionStage';
import DaysAndHoursInput from './DaysAndHoursInput';
import PrivacyFooter from './PrivacyFooter';
import ScaleInput from './ScaleInput';
import { useWellbeingMeasureSurvey } from './useWellbeingMeasureSurvey';
import './WellbeingMeasureModal.scss';

/** Matches the designs' pause before moving on, so the choice registers. */
const AUTO_ADVANCE_MS = 300;

/**
 * The survey card's own surface and width.
 *
 * `Shared/Modal` forwards its `className` straight onto the styled-component
 * `ModalCard`, which is what makes `styled(Modal)` the supported way to
 * restyle the card itself. Both declarations override one `ModalCard` rule
 * each, so they are wrapped in `&&` to double the specificity: styled-
 * components' injection order between a wrapper and the component it wraps
 * is not guaranteed, and equal specificity would leave this decided by that
 * order.
 *
 * `colors.modal.background` is the surface token — white on both channels.
 * `ModalCard`'s own `background.primary` is the page background behind a
 * modal (`#F2EDE3` on Unmind), not a card surface.
 *
 * The width is wider than `size="medium"` (528px/605px) so the footer's
 * privacy line and the Next button share one row in en-GB. Longer locales
 * still wrap, which is why the footer is allowed to rather than being
 * pinned to one line. `ModalCard` keeps its own `max-width: 90%`, so narrow
 * viewports are unaffected.
 */
const WellbeingMeasureModalCard = styled(Modal)`
  && {
    width: 640px;
    background: ${({ theme }) => theme.colors.modal.background};
  }
`;

/**
 * Adds the prototype's header divider. `.wellbeing-measure-modal__header`
 * stays on the element for its existing grid layout — this only supplies the
 * one colour Sass has no token for. Matches `Shared/Modal`'s own `ModalCard`
 * border, which uses the same token on this element's containing card.
 */
const HeaderWithDivider = styled.header`
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.secondary};
`;

/**
 * core-api's `DuplicateError` reports itself through
 * `extensions.exception.name`, not through `extensions.code` — verified in
 * `lib/applicationErrors.ts`.
 */
const isDuplicateSubmission = (error: unknown) =>
  error instanceof ApolloError &&
  error.graphQLErrors.some(
    graphQLError =>
      (graphQLError.extensions?.exception as { name?: string } | undefined)
        ?.name === 'DuplicateError',
  );

export type WellbeingMeasureModalProps = {
  questionnaire: Questionnaire;
  entryPoint: WellbeingMeasureEntryPoint;
  /** Focused when the modal closes, so keyboard users are not dumped at the
   * top of the page. */
  returnFocusTo?: React.RefObject<HTMLElement>;
  onClose(): void;
  onComplete(): void;
};

const WellbeingMeasureModal = ({
  questionnaire,
  entryPoint,
  returnFocusTo,
  onClose,
  onComplete,
}: WellbeingMeasureModalProps) => {
  const { t: translate } = useTranslation('homepage');
  const questionHeadingRef = useRef<HTMLHeadingElement>(null);
  const autoAdvanceTimer = useRef<ReturnType<typeof setTimeout>>();
  const isMounted = useRef(true);
  /** The days-and-hours figures last reported for each question. */
  const trackedDaysAndHours = useRef<Record<string, string>>({});
  const survey = useWellbeingMeasureSurvey(questionnaire.questions);
  const {
    step,
    currentQuestion,
    currentAnswer,
    startedAt,
    answers,
    autoAdvances,
    isFinalQuestion,
    answer,
    goBack,
    goForward,
  } = survey;

  const baseProperties = { entryPoint, questionnaireId: questionnaire.id };
  const total = questionnaire.questions.length;

  const trackAnswered = (question: NonNullable<typeof currentQuestion>) =>
    tracking.track('wellbeing-measure-question-answered', {
      ...baseProperties,
      questionId: question.id,
      position: step + 1,
      total,
      uiType: question.uiType,
    });

  const unsupportedUiType =
    currentQuestion &&
    currentQuestion.uiType !== INDIVIDUAL_UI_TYPE &&
    currentQuestion.uiType !== DAYS_AND_HOURS_UI_TYPE
      ? currentQuestion.uiType
      : undefined;

  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [hasFailed, setHasFailed] = useState(false);

  /**
   * `Shared/Modal` declares `returnFocusTo` but never reads it — its only
   * focus-return path is the `openModalButton` render prop, which this modal
   * does not use. Calling `.focus()` here is the accepted approach rather
   * than changing shared code.
   */
  const returnFocus = () => returnFocusTo?.current?.focus();

  // Declared above `handleClose` because the close path has to know whether a
  // save is already in flight.
  const [save, { loading: isSaving }] = useMutation<
    SaveWellbeingMeasureResponses,
    SaveWellbeingMeasureResponsesVariables
  >(SAVE_WELLBEING_MEASURE_RESPONSES_MUTATION, {
    // Runs even if the modal is closed while saving, so anything showing the
    // status still learns the survey is done.
    refetchQueries: [HOME_WELLBEING_MEASURE_STATUS_QUERY],
  });

  /**
   * Only ever wired to the pre-submission modal — the submitted branch closes
   * through `handleComplete` — so reaching here means the member left before
   * the thank-you state.
   *
   * Leaving while the save is in flight is not an abandonment: the answers are
   * already on their way and `onSubmit` reports the completion regardless of
   * whether this modal is still mounted, so tracking both would count one
   * submission twice and understate the completion rate. The guard lives here
   * rather than on the close button because `Shared/Modal` also closes on Esc
   * and backdrop clicks — and a hung request should never leave a member with
   * no way out of a wellbeing survey.
   */
  const handleClose = () => {
    if (currentQuestion && !isSaving) {
      tracking.track('wellbeing-measure-abandoned', {
        ...baseProperties,
        questionId: currentQuestion.id,
        position: step + 1,
        total,
      });
    }

    returnFocus();
    onClose();
  };

  const handleComplete = () => {
    returnFocus();
    onComplete();
  };

  const progressPercentage = ((step + 1) / total) * 100;

  /**
   * Built by mapping over the questionnaire's questions, so submission order
   * follows the question set rather than the order the member happened to
   * answer in. Every question is guaranteed an answer here — the footer
   * button can't advance without one.
   */
  const toAnswerInputs = () =>
    questionnaire.questions.map(question => {
      const entry = answers.find(
        candidate => candidate.questionId === question.id,
      );

      if (!entry) {
        throw new Error(
          `Missing Wellbeing Measure answer for question ${question.id}`,
        );
      }

      return {
        questionId: question.id,
        answer:
          entry.kind === 'daysAndHours'
            ? { daysAndHours: { days: entry.days, hours: entry.hours } }
            : { numeric: entry.value },
      };
    });

  const onSubmit = async () => {
    setHasFailed(false);

    try {
      const { data } = await save({
        variables: {
          input: {
            questionnaireId: questionnaire.id,
            startedAt,
            completedAt: new Date().toISOString(),
            entryPoint,
            answers: toAnswerInputs(),
          },
        },
      });

      const payload = data?.saveWellbeingMeasureResponses ?? null;

      // Tracked before the mount guard so a member who closes the modal while
      // the mutation is in flight still reports the completion that landed.
      if (payload?.success) {
        void multiDestinationLogger({
          eventName: 'wellbeing-measure-completed',
          properties: baseProperties,
        });
      }

      if (!isMounted.current) {
        return;
      }

      // A resolved mutation is not a saved one: the payload reports failure
      // through `success`/`code` rather than by throwing, so treating any
      // non-throwing response as stored would show the thank-you state over
      // answers that never landed.
      if (!payload?.success) {
        // core-api reports an already-recorded submission either as a
        // `DuplicateError` (caught below) or as this code. Both mean the
        // answers are safe, so neither should invite a second attempt.
        if (payload?.code === MutationStatusCode.DUPLICATE_ENTRY) {
          setHasSubmitted(true);

          return;
        }

        setHasFailed(true);

        return;
      }

      setHasSubmitted(true);
    } catch (error) {
      if (!isMounted.current) {
        return;
      }

      // The submission already landed; showing an error would only invite a
      // second attempt.
      if (isDuplicateSubmission(error)) {
        setHasSubmitted(true);

        return;
      }

      setHasFailed(true);
    }
  };

  // Logging from `renderInput` would fire on every re-render and make render
  // impure, so it happens here instead.
  useEffect(() => {
    if (!unsupportedUiType) {
      return;
    }

    logException(
      new Error(`Unrecognised Wellbeing Measure uiType: ${unsupportedUiType}`),
    );
  }, [unsupportedUiType]);

  // The modal is mounted only while open, and ENG-21846's direct link renders
  // the same component with its own `entryPoint`, so one effect here covers
  // every way the survey can be started.
  useEffect(() => {
    void multiDestinationLogger({
      eventName: 'wellbeing-measure-started',
      properties: baseProperties,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Move focus to the new question so a screen reader announces the change
  // rather than leaving the user on a button that has just vanished.
  useEffect(() => {
    questionHeadingRef.current?.focus();
  }, [step]);

  // Clears any pending auto-advance on unmount, so answering a question and
  // closing the modal inside the 300ms window doesn't call `goForward` (and
  // set state) after the hook instance is gone.
  useEffect(
    () => () => {
      clearTimeout(autoAdvanceTimer.current);
    },
    [],
  );

  // A member can close the modal while a submission is still in flight, so
  // `onSubmit` checks this ref before setting state. Set it to `true` here as
  // well as clearing it on unmount: React can re-run an effect on the same
  // component, and a cleanup-only effect would leave the ref stuck `false`
  // and silently drop a successful submission.
  useEffect(() => {
    isMounted.current = true;

    return () => {
      isMounted.current = false;
    };
  }, []);

  const onScaleChange = (value: number) => {
    if (!currentQuestion) {
      return;
    }

    // Re-picking the answer already showing — the way a member who stepped
    // back moves forward again — still has to advance, but it is not a second
    // answer, so it must not report a second event.
    const isSameAnswer =
      currentAnswer?.kind === 'numeric' && currentAnswer.value === value;

    answer({ kind: 'numeric', questionId: currentQuestion.id, value });

    if (!isSameAnswer) {
      trackAnswered(currentQuestion);
    }

    if (autoAdvances) {
      // Clear any still-pending timer first: answering a second question
      // before the first's timer fires must not leave the earlier one to
      // fire on top of it.
      clearTimeout(autoAdvanceTimer.current);
      autoAdvanceTimer.current = setTimeout(goForward, AUTO_ADVANCE_MS);
    }
  };

  // Back must cancel a pending auto-advance: answering and hitting Back
  // inside the 300ms window would otherwise step back and then immediately
  // forward again, landing the member one question ahead of where they asked
  // to go.
  const onBack = () => {
    clearTimeout(autoAdvanceTimer.current);
    goBack();
  };

  /**
   * The footer button, which is shown on questions that don't auto-advance.
   *
   * It is also where a days-and-hours answer is reported: the input reports
   * every change, so the moment the member moves on is the only point the
   * answer is settled. The scale inputs report themselves on selection, so
   * they are not tracked again here.
   *
   * The last reported figures are kept per question because stepping back
   * and moving on again re-confirms an answer that is already recorded —
   * the same reasoning as the re-picked scale answer above.
   */
  const onConfirm = () => {
    if (
      currentQuestion?.uiType === DAYS_AND_HOURS_UI_TYPE &&
      currentAnswer?.kind === 'daysAndHours'
    ) {
      const reported = `${currentAnswer.days}:${currentAnswer.hours}`;

      if (trackedDaysAndHours.current[currentQuestion.id] !== reported) {
        trackedDaysAndHours.current[currentQuestion.id] = reported;
        trackAnswered(currentQuestion);
      }
    }

    if (isFinalQuestion) {
      void onSubmit();

      return;
    }

    goForward();
  };

  const renderInput = () => {
    if (!currentQuestion) {
      return null;
    }

    if (currentQuestion.uiType === INDIVIDUAL_UI_TYPE) {
      return (
        <ScaleInput
          questionId={currentQuestion.id}
          legend={currentQuestion.text}
          // The question text is already announced by the `<h2>` above
          // (`id="modal-title"`); labelling the fieldset by that heading
          // instead of rendering ScaleInput's own hidden legend avoids the
          // question being announced twice.
          labelledBy="modal-title"
          minimumValue={currentQuestion.scale?.minimumValue ?? 0}
          maximumValue={currentQuestion.scale?.maximumValue ?? 0}
          stepSize={currentQuestion.scale?.stepSize}
          minimumLabel={currentQuestion.scale?.minimumLabel ?? null}
          maximumLabel={currentQuestion.scale?.maximumLabel ?? null}
          value={currentAnswer?.kind === 'numeric' ? currentAnswer.value : null}
          onChange={onScaleChange}
        />
      );
    }

    if (currentQuestion.uiType === DAYS_AND_HOURS_UI_TYPE) {
      return (
        <DaysAndHoursInput
          assumedWorkingDaysPerWeek={questionnaire.assumedWorkingDaysPerWeek}
          assumedWorkingHoursPerDay={questionnaire.assumedWorkingHoursPerDay}
          days={currentAnswer?.kind === 'daysAndHours' ? currentAnswer.days : 0}
          hours={
            currentAnswer?.kind === 'daysAndHours' ? currentAnswer.hours : 0
          }
          // No tracking here: this reports every change to either field, so
          // an answer would be counted once per keystroke. The question has
          // no auto-advance, so `onConfirm` reports it instead.
          onChange={next => {
            answer({
              kind: 'daysAndHours',
              questionId: currentQuestion.id,
              ...next,
            });
          }}
        />
      );
    }

    return null;
  };

  if (hasSubmitted) {
    return (
      <WellbeingMeasureModalCard
        open
        size="medium"
        closeHeader={false}
        onCloseCallback={handleComplete}
        modalContent={({ close }) => (
          <div className="wellbeing-measure-modal">
            <header className="wellbeing-measure-modal__completion-header">
              <span className="wellbeing-measure-modal__close-button-slot">
                <Button
                  variant="tertiary"
                  size="small"
                  icon="close"
                  screenreaderText={translate('wellbeing_measure.modal.close')}
                  onClick={close}
                />
              </span>
            </header>
            <CompletionStage onDone={handleComplete} />
          </div>
        )}
      />
    );
  }

  return (
    <WellbeingMeasureModalCard
      open
      size="medium"
      closeHeader={false}
      onCloseCallback={handleClose}
      modalContent={({ close }) => (
        <div className="wellbeing-measure-modal">
          <HeaderWithDivider className="wellbeing-measure-modal__header">
            {/* The design system `Button` renders two sibling DOM nodes when given
                `screenreaderText` (the `<button>` plus a hidden `<span>`
                label), so passing it directly as a grid child gives the
                three-column header five items instead of three. Wrapping
                each icon button in a single `<span>` keeps the grid to
                exactly one item per column. */}
            <span
              className="wellbeing-measure-modal__back-button-slot"
              data-testid="wellbeing-measure-back-button-slot"
              // `visibility`, not `display`: the header must not reflow
              // between question one and question two.
              style={{ visibility: step === 0 ? 'hidden' : 'visible' }}
            >
              <Button
                variant="tertiary"
                size="small"
                icon="chevron-left"
                screenreaderText={translate('wellbeing_measure.modal.back')}
                onClick={onBack}
                data-testid="wellbeing-measure-back-button"
              />
            </span>
            <div className="wellbeing-measure-modal__progress">
              {/* The DS ProgressCircle (@unmind/design-system@4.38.3) only
                  sets `data-progress` on the root element, but its own CSS
                  draws the arc and end cap from a `--pct` custom property
                  that nothing in the component ever sets — without it the
                  conic-gradient resolves fully transparent and the ring
                  never appears. This is a props-level workaround for that DS
                  defect, not stray styling — don't delete it. */}
              <ProgressCircle
                size="small"
                percentage={progressPercentage}
                style={{ '--pct': progressPercentage } as React.CSSProperties}
                aria-hidden="true"
              />
              <span className="label-m">
                {translate('wellbeing_measure.modal.progress', {
                  current: step + 1,
                  total,
                })}
              </span>
            </div>
            <span className="wellbeing-measure-modal__close-button-slot">
              <Button
                variant="tertiary"
                size="small"
                icon="close"
                screenreaderText={translate('wellbeing_measure.modal.close')}
                onClick={close}
              />
            </span>
          </HeaderWithDivider>

          <div className="wellbeing-measure-modal__question">
            {currentQuestion?.contextText ? (
              <p className="body-s">{currentQuestion.contextText}</p>
            ) : null}
            {/* `Shared/Modal` hardcodes `aria-labelledby="modal-title"`, so
                the dialog has no accessible name unless something carries that
                id — `closeHeader={false}` means its own `ModalTitle` is not
                rendered. */}
            <h2
              id="modal-title"
              className="title-m"
              tabIndex={-1}
              ref={questionHeadingRef}
            >
              {currentQuestion?.text}
            </h2>
          </div>

          <div className="wellbeing-measure-modal__control">
            {renderInput()}
          </div>

          {hasFailed ? (
            <div className="wellbeing-measure-modal__error">
              <Alert variant="error" size="small">
                <strong>
                  {translate('wellbeing_measure.submission_error_title')}
                </strong>
                <br />
                {translate('wellbeing_measure.submission_error_body')}
              </Alert>
            </div>
          ) : null}

          <footer className="wellbeing-measure-modal__footer">
            <PrivacyFooter />
            {!autoAdvances ? (
              <Button
                variant="primary"
                size="medium"
                label={translate(
                  isFinalQuestion
                    ? 'wellbeing_measure.modal.submit'
                    : 'wellbeing_measure.modal.next',
                )}
                disabled={!currentAnswer}
                loading={isFinalQuestion ? isSaving : false}
                onClick={onConfirm}
              />
            ) : null}
          </footer>
        </div>
      )}
    />
  );
};

export default WellbeingMeasureModal;
